import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { BlocksRenderer } from '../components/BlockRenderer';
import { isValidLocale } from '../i18n';
import { Loader2 } from 'lucide-react';

/**
 * Custom CMS page route — /:lang/p/:slug
 * Resolves the route slug to a custom page (created by admin) and renders
 * its blocks. Tracks a view on mount.
 */
export default function CustomPage() {
  const { lang, slug } = useParams();
  const locale = lang && isValidLocale(lang) ? lang : 'es';
  const [state, setState] = useState({ loading: true, page: null, error: '' });

  useEffect(() => {
    let alive = true;
    setState({ loading: true, page: null, error: '' });
    api.get(`/pages/by-route/${slug}`)
      .then(({ data }) => {
        if (!alive) return;
        setState({ loading: false, page: data, error: '' });
        // Fire-and-forget view tracking
        api.post(`/pages/${data.page_key}/track-view`, { locale, referrer: document.referrer || '' }).catch(() => {});
        document.title = `${(data[`label_${locale}`] || data.label_es || slug)} · CMIC`;
      })
      .catch(() => {
        if (alive) setState({ loading: false, page: null, error: 'not_found' });
      });
    return () => { alive = false; };
  }, [slug, locale]);

  if (state.loading) {
    return (
      <div className="flex items-center justify-center py-32" data-testid="custom-page-loading">
        <Loader2 className="animate-spin text-cmic-500" size={28} />
      </div>
    );
  }

  if (state.error || !state.page) {
    return (
      <div className="max-w-2xl mx-auto px-4 py-24 text-center" data-testid="custom-page-not-found">
        <h1 className="text-3xl font-bold text-charcoal-800 mb-3">
          {locale === 'en' ? 'Page not found' : 'Página no encontrada'}
        </h1>
        <p className="text-charcoal-500 mb-6">
          {locale === 'en' ? 'The page you are looking for does not exist.' : 'La página que buscas no existe.'}
        </p>
        <Link to={`/${locale}`} className="inline-flex items-center text-cmic-600 hover:text-cmic-700 font-semibold">
          {locale === 'en' ? '← Back to home' : '← Volver al inicio'}
        </Link>
      </div>
    );
  }

  const title = state.page[`label_${locale}`] || state.page.label_es;
  const blocks = state.page.blocks || [];

  return (
    <div data-testid="custom-page" data-page-key={state.page.page_key}>
      {blocks.length === 0 && (
        <div className="max-w-2xl mx-auto px-4 py-20 text-center">
          <h1 className="text-3xl font-bold text-charcoal-800 mb-3">{title}</h1>
          <p className="text-charcoal-500">
            {locale === 'en' ? 'This page has no content yet.' : 'Esta página aún no tiene contenido.'}
          </p>
        </div>
      )}
      <BlocksRenderer blocks={blocks} locale={locale} />
    </div>
  );
}
